/**
 * Theme management — light/dark, persisted in localStorage.
 *
 * The `data-theme` attribute on `<html>` drives all CSS variables
 * (see index.css). Default is `dark` (set in index.html so the page
 * doesn't flash white before JS hydrates).
 */

import { useEffect, useState } from "react";

export type Theme = "dark" | "light";

const STORAGE_KEY = "tgp.theme";

function readStoredTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return "dark";
}

function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

export function useTheme(): {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
} {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    applyTheme(theme);
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  return {
    theme,
    setTheme: setThemeState,
    toggle: () => setThemeState((t) => (t === "dark" ? "light" : "dark")),
  };
}

/** Apply the stored theme as early as possible to avoid flash-of-wrong-theme.
 * Call this before React renders. */
export function bootstrapTheme(): void {
  applyTheme(readStoredTheme());
}
